// BlackBox Game Engine — Core game logic (no React dependencies)

const GAME_CONFIG = {
    MAP_WIDTH: 1200,
    MAP_HEIGHT: 800,
    PLAYER_RADIUS: 16,
    PLAYER_SPEED: 5,
    BULLET_SPEED: 10,
    BULLET_RADIUS: 5,
    BULLET_LIFETIME: 80,
    PLAYER_MAX_HP: 3,
    SHOOT_COOLDOWN: 300, // ms
    GAME_DURATION: 120, // seconds
    POWERUP_SPAWN_INTERVAL: 8000, // ms
    POWERUP_RADIUS: 8,
    // Flashlight
    FLASHLIGHT_RANGE: 220,
    FLASHLIGHT_CONE: Math.PI / 3.5, // ~51 degree cone
    FLASHLIGHT_BOOST_RANGE: 320,
    FLASHLIGHT_BOOST_DURATION: 5000,
    AMBIENT_GLOW: 35, // tiny radial glow so player isn't fully blind
    BULLET_LIGHT_RADIUS: 25,
    // Dust
    DUST_COUNT: 60,
};

// Grayscale/monochrome player tones for silhouette look
const PLAYER_COLORS = [
    '#ffffff', '#e0e0e0', '#c0c0c0', '#a0a0a0',
    '#d0d0d0', '#b0b0b0', '#f0f0f0', '#909090',
    '#e8e8e8', '#d8d8d8', '#c8c8c8', '#b8b8b8',
];

// Dense corridor/room map layout — like an underground bunker
const OBSTACLE_PRESETS = [
    // Outer walls / thick borders
    { x: 0, y: 0, w: 1200, h: 12 },      // top wall
    { x: 0, y: 788, w: 1200, h: 12 },     // bottom wall
    { x: 0, y: 0, w: 12, h: 800 },        // left wall
    { x: 1188, y: 0, w: 12, h: 800 },     // right wall

    // Central pillar cluster
    { x: 560, y: 360, w: 80, h: 80 },

    // Top-left room
    { x: 80, y: 80, w: 200, h: 14 },      // top wall
    { x: 80, y: 80, w: 14, h: 150 },      // left wall
    { x: 80, y: 216, w: 120, h: 14 },     // bottom wall (gap for door)

    // Top corridor horizontal
    { x: 350, y: 120, w: 14, h: 140 },    // corridor left wall
    { x: 500, y: 120, w: 14, h: 100 },    // corridor right wall
    { x: 350, y: 120, w: 165, h: 14 },    // corridor top

    // Top-right area  
    { x: 700, y: 60, w: 14, h: 180 },     // divider
    { x: 700, y: 60, w: 200, h: 14 },     // top
    { x: 886, y: 60, w: 14, h: 120 },     // right partial
    { x: 800, y: 160, w: 100, h: 14 },    // shelf

    // Middle-left corridor
    { x: 80, y: 340, w: 180, h: 14 },     // top wall
    { x: 80, y: 420, w: 130, h: 14 },     // bottom wall (gap)

    // Middle maze section
    { x: 320, y: 300, w: 14, h: 120 },    // vertical wall
    { x: 420, y: 280, w: 14, h: 80 },     // vertical wall
    { x: 320, y: 300, w: 115, h: 14 },    // horizontal wall

    // Crate clusters — different sizes
    { x: 160, y: 520, w: 40, h: 40 },     // crate
    { x: 210, y: 540, w: 30, h: 30 },     // small crate
    { x: 170, y: 570, w: 35, h: 25 },     // crate

    // Bottom-left room
    { x: 60, y: 580, w: 14, h: 180 },     // left wall
    { x: 60, y: 580, w: 160, h: 14 },     // top wall
    { x: 206, y: 580, w: 14, h: 120 },    // right wall (gap at bottom)

    // Bottom corridor
    { x: 300, y: 600, w: 300, h: 14 },    // top wall
    { x: 300, y: 700, w: 200, h: 14 },    // bottom wall (gap)
    { x: 400, y: 650, w: 60, h: 14 },     // obstacle in corridor

    // Right side structures
    { x: 900, y: 300, w: 14, h: 200 },    // tall vertical wall
    { x: 900, y: 300, w: 120, h: 14 },    // top
    { x: 1006, y: 300, w: 14, h: 140 },   // right wall
    { x: 940, y: 380, w: 80, h: 14 },     // shelf inside

    // Bottom-right area
    { x: 800, y: 560, w: 200, h: 14 },    // top wall
    { x: 800, y: 560, w: 14, h: 180 },    // left wall
    { x: 986, y: 600, w: 14, h: 140 },    // right wall partial
    { x: 850, y: 640, w: 50, h: 50 },     // box

    // Scattered pillars / cover
    { x: 480, y: 500, w: 30, h: 30 },
    { x: 680, y: 450, w: 25, h: 50 },
    { x: 750, y: 280, w: 40, h: 20 },
    { x: 550, y: 550, w: 20, h: 60 },
    { x: 1050, y: 200, w: 50, h: 30 },
    { x: 1080, y: 650, w: 30, h: 60 },
];

// Ambient light sources (lanterns) placed in the map
const AMBIENT_LIGHTS = [
    { x: 160, y: 150, radius: 40, intensity: 0.25 },   // top-left room
    { x: 600, y: 400, radius: 35, intensity: 0.2 },    // center
    { x: 950, y: 350, radius: 30, intensity: 0.15 },   // right room
    { x: 130, y: 650, radius: 35, intensity: 0.2 },    // bottom-left
    { x: 450, y: 650, radius: 30, intensity: 0.15 },   // bottom corridor
    { x: 900, y: 630, radius: 35, intensity: 0.2 },    // bottom-right
    { x: 780, y: 130, radius: 25, intensity: 0.12 },   // top-right
    { x: 400, y: 200, radius: 30, intensity: 0.15 },   // top corridor
];

class Player {
    constructor(id, username, x, y, colorIndex, skinId = null) {
        this.id = id;
        this.username = username;
        this.x = x;
        this.y = y;
        this.angle = 0;
        this.color = PLAYER_COLORS[colorIndex % PLAYER_COLORS.length];
        this.skinId = skinId || ['shadow', 'phantom', 'ember', 'specter', 'wraith'][colorIndex % 5];
        this.hp = GAME_CONFIG.PLAYER_MAX_HP;
        this.kills = 0;
        this.alive = true;
        this.flashlightRange = GAME_CONFIG.FLASHLIGHT_RANGE;
        this.flashlightBoostEnd = 0;
        this.lastShot = 0;
        this.vx = 0;
        this.vy = 0;
    }
}

class Bullet {
    constructor(id, ownerId, x, y, angle, color) {
        this.id = id;
        this.ownerId = ownerId;
        this.x = x;
        this.y = y;
        this.vx = Math.cos(angle) * GAME_CONFIG.BULLET_SPEED;
        this.vy = Math.sin(angle) * GAME_CONFIG.BULLET_SPEED;
        this.color = color;
        this.life = GAME_CONFIG.BULLET_LIFETIME;
        this.trail = [];
    }
}

class Particle {
    constructor(x, y, color) {
        this.x = x;
        this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = 1 + Math.random() * 3;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.color = color;
        this.life = 20 + Math.random() * 20;
        this.maxLife = this.life;
        this.radius = 2 + Math.random() * 3;
    }
}

// Dust mote — passive floating particle
class DustMote {
    constructor(mapW, mapH) {
        this.x = Math.random() * mapW;
        this.y = Math.random() * mapH;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.2 - 0.1; // slight upward drift
        this.radius = 0.5 + Math.random() * 1.5;
        this.alpha = 0.2 + Math.random() * 0.4;
        this.phase = Math.random() * Math.PI * 2;
        this.mapW = mapW;
        this.mapH = mapH;
    }

    update() {
        this.x += this.vx + Math.sin(Date.now() * 0.001 + this.phase) * 0.15;
        this.y += this.vy;
        // Wrap around
        if (this.x < 0) this.x = this.mapW;
        if (this.x > this.mapW) this.x = 0;
        if (this.y < 0) this.y = this.mapH;
        if (this.y > this.mapH) this.y = 0;
    }
}

class Powerup {
    constructor(x, y, type) {
        this.id = `pw_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        this.x = x;
        this.y = y;
        this.type = type; // 'health' or 'light'
        this.pulsePhase = Math.random() * Math.PI * 2;
    }
}

// Ray-segment intersection for shadow casting
function raySegmentIntersect(ox, oy, dx, dy, x1, y1, x2, y2) {
    const sx = x2 - x1;
    const sy = y2 - y1;
    const denom = dx * sy - dy * sx;
    if (Math.abs(denom) < 0.0001) return null;
    const t = ((x1 - ox) * sy - (y1 - oy) * sx) / denom;
    const u = ((x1 - ox) * dy - (y1 - oy) * dx) / denom;
    if (t > 0 && u >= 0 && u <= 1) return t;
    return null;
}

// Cast rays to build visibility polygon for flashlight cone
function castFlashlight(px, py, angle, coneAngle, range, obstacles) {
    const points = [];
    const numRays = 90; // balance quality vs performance
    const halfCone = coneAngle / 2;
    const startAngle = angle - halfCone;
    const step = coneAngle / numRays;

    // Build edge list from obstacles
    const edges = [];
    for (const obs of obstacles) {
        const x1 = obs.x, y1 = obs.y;
        const x2 = obs.x + obs.w, y2 = obs.y;
        const x3 = obs.x + obs.w, y3 = obs.y + obs.h;
        const x4 = obs.x, y4 = obs.y + obs.h;
        edges.push([x1, y1, x2, y2]);
        edges.push([x2, y2, x3, y3]);
        edges.push([x3, y3, x4, y4]);
        edges.push([x4, y4, x1, y1]);
    }

    for (let i = 0; i <= numRays; i++) {
        const a = startAngle + i * step;
        const dx = Math.cos(a);
        const dy = Math.sin(a);
        let closest = range;

        for (const [ex1, ey1, ex2, ey2] of edges) {
            const t = raySegmentIntersect(px, py, dx, dy, ex1, ey1, ex2, ey2);
            if (t !== null && t < closest) {
                closest = t;
            }
        }

        points.push({
            x: px + dx * closest,
            y: py + dy * closest
        });
    }

    return points;
}

class GameEngine {
    constructor() {
        this.players = new Map();
        this.bullets = [];
        this.particles = [];
        this.powerups = [];
        this.obstacles = OBSTACLE_PRESETS;
        this.ambientLights = AMBIENT_LIGHTS;
        this.dustMotes = [];
        this.localPlayerId = null;
        this.gameState = 'lobby'; // lobby, playing, ended
        this.startTime = 0;
        this.timeRemaining = GAME_CONFIG.GAME_DURATION;
        this.killFeed = [];
        this.lastPowerupSpawn = 0;
        this.bulletIdCounter = 0;

        // Initialize dust
        for (let i = 0; i < GAME_CONFIG.DUST_COUNT; i++) {
            this.dustMotes.push(new DustMote(GAME_CONFIG.MAP_WIDTH, GAME_CONFIG.MAP_HEIGHT));
        }
    }

    addPlayer(id, username, colorIndex, skinId = null) {
        const spawn = this.getRandomSpawn();
        const player = new Player(id, username, spawn.x, spawn.y, colorIndex, skinId);
        this.players.set(id, player);
        return player;
    }

    removePlayer(id) {
        this.players.delete(id);
    }

    getRandomSpawn() {
        const margin = 40;
        for (let attempt = 0; attempt < 100; attempt++) {
            const x = margin + Math.random() * (GAME_CONFIG.MAP_WIDTH - 2 * margin);
            const y = margin + Math.random() * (GAME_CONFIG.MAP_HEIGHT - 2 * margin);
            let ok = true;
            for (const obs of this.obstacles) {
                if (x > obs.x - 20 && x < obs.x + obs.w + 20 && y > obs.y - 20 && y < obs.y + obs.h + 20) {
                    ok = false;
                    break;
                }
            }
            if (ok) return { x, y };
        }
        return { x: GAME_CONFIG.MAP_WIDTH / 2, y: GAME_CONFIG.MAP_HEIGHT / 2 };
    }

    startGame() {
        this.gameState = 'playing';
        this.startTime = Date.now();
        this.timeRemaining = GAME_CONFIG.GAME_DURATION;
        this.bullets = [];
        this.particles = [];
        this.powerups = [];
        this.killFeed = [];
        this.players.forEach((player) => {
            const spawn = this.getRandomSpawn();
            player.x = spawn.x;
            player.y = spawn.y;
            player.hp = GAME_CONFIG.PLAYER_MAX_HP;
            player.kills = 0;
            player.alive = true;
        });
    }

    updateLocalPlayer(keys, mouseAngle) {
        const player = this.players.get(this.localPlayerId);
        if (!player || !player.alive || this.gameState !== 'playing') return null;

        let vx = 0, vy = 0;
        if (keys.w || keys.arrowup) vy = -1;
        if (keys.s || keys.arrowdown) vy = 1;
        if (keys.a || keys.arrowleft) vx = -1;
        if (keys.d || keys.arrowright) vx = 1;

        if (vx !== 0 && vy !== 0) {
            const len = Math.sqrt(vx * vx + vy * vy);
            vx /= len;
            vy /= len;
        }

        player.vx = vx * GAME_CONFIG.PLAYER_SPEED;
        player.vy = vy * GAME_CONFIG.PLAYER_SPEED;
        player.angle = mouseAngle;

        const newX = player.x + player.vx;
        const newY = player.y + player.vy;

        player.x = Math.max(GAME_CONFIG.PLAYER_RADIUS + 12, Math.min(GAME_CONFIG.MAP_WIDTH - GAME_CONFIG.PLAYER_RADIUS - 12, newX));
        player.y = Math.max(GAME_CONFIG.PLAYER_RADIUS + 12, Math.min(GAME_CONFIG.MAP_HEIGHT - GAME_CONFIG.PLAYER_RADIUS - 12, newY));

        // Obstacle collision
        for (const obs of this.obstacles) {
            const closestX = Math.max(obs.x, Math.min(player.x, obs.x + obs.w));
            const closestY = Math.max(obs.y, Math.min(player.y, obs.y + obs.h));
            const dx = player.x - closestX;
            const dy = player.y - closestY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < GAME_CONFIG.PLAYER_RADIUS) {
                const overlap = GAME_CONFIG.PLAYER_RADIUS - dist;
                if (dist > 0) {
                    player.x += (dx / dist) * overlap;
                    player.y += (dy / dist) * overlap;
                }
            }
        }

        // Flashlight boost
        if (Date.now() < player.flashlightBoostEnd) {
            player.flashlightRange = GAME_CONFIG.FLASHLIGHT_BOOST_RANGE;
        } else {
            player.flashlightRange = GAME_CONFIG.FLASHLIGHT_RANGE;
        }

        // Powerup pickups
        this.powerups = this.powerups.filter(pw => {
            const dx = player.x - pw.x;
            const dy = player.y - pw.y;
            if (Math.sqrt(dx * dx + dy * dy) < GAME_CONFIG.PLAYER_RADIUS + GAME_CONFIG.POWERUP_RADIUS) {
                if (pw.type === 'health' && player.hp < GAME_CONFIG.PLAYER_MAX_HP) {
                    player.hp = Math.min(GAME_CONFIG.PLAYER_MAX_HP, player.hp + 1);
                    this.spawnParticles(pw.x, pw.y, '#aaa', 8);
                    return false;
                } else if (pw.type === 'light') {
                    player.flashlightBoostEnd = Date.now() + GAME_CONFIG.FLASHLIGHT_BOOST_DURATION;
                    this.spawnParticles(pw.x, pw.y, '#fff', 8);
                    return false;
                }
            }
            return true;
        });

        return { x: player.x, y: player.y, angle: player.angle };
    }

    shoot(playerId) {
        const player = this.players.get(playerId);
        if (!player || !player.alive) return null;

        const now = Date.now();
        if (now - player.lastShot < GAME_CONFIG.SHOOT_COOLDOWN) return null;
        player.lastShot = now;

        const bulletId = `b_${playerId}_${this.bulletIdCounter++}`;
        const offsetX = Math.cos(player.angle) * (GAME_CONFIG.PLAYER_RADIUS + 5);
        const offsetY = Math.sin(player.angle) * (GAME_CONFIG.PLAYER_RADIUS + 5);

        const bullet = new Bullet(
            bulletId, playerId,
            player.x + offsetX, player.y + offsetY,
            player.angle, '#fff' // white bullets for monochrome
        );
        this.bullets.push(bullet);
        // Muzzle flash particles
        this.spawnParticles(bullet.x, bullet.y, '#fff', 3);
        return { id: bulletId, x: bullet.x, y: bullet.y, angle: player.angle, color: '#fff' };
    }

    addRemoteBullet(data) {
        console.log('[Engine] addRemoteBullet called:', data.ownerId, 'at', data.x, data.y, 'angle:', data.angle);
        const bullet = new Bullet(data.id, data.ownerId, data.x, data.y, data.angle, data.color || '#fff');
        this.bullets.push(bullet);
        console.log('[Engine] Total bullets now:', this.bullets.length, 'Remote bullets:', this.bullets.filter(b => b.ownerId !== this.localPlayerId).length);
    }

    updateBullets() {
        this.bullets = this.bullets.filter(bullet => {
            bullet.trail.push({ x: bullet.x, y: bullet.y });
            if (bullet.trail.length > 6) bullet.trail.shift();

            bullet.x += bullet.vx;
            bullet.y += bullet.vy;
            bullet.life--;

            if (bullet.x < 0 || bullet.x > GAME_CONFIG.MAP_WIDTH ||
                bullet.y < 0 || bullet.y > GAME_CONFIG.MAP_HEIGHT) {
                return false;
            }

            for (const obs of this.obstacles) {
                if (bullet.x > obs.x && bullet.x < obs.x + obs.w &&
                    bullet.y > obs.y && bullet.y < obs.y + obs.h) {
                    this.spawnParticles(bullet.x, bullet.y, '#888', 4);
                    return false;
                }
            }

            // NOTE: Player collision is handled EXCLUSIVELY by checkLocalPlayerHit()
            // Do NOT check player collision here — it removes bullets before hit detection runs

            return bullet.life > 0;
        });
    }

    // Shooter-authoritative hit detection:
    // 1. Check remote bullets vs local player (victim reports)
    // 2. Check LOCAL bullets vs REMOTE players (shooter reports) — this is the key fix!
    checkAllHits() {
        const hits = [];
        const localPlayer = this.players.get(this.localPlayerId);
        if (!localPlayer) return hits;

        const threshold = GAME_CONFIG.PLAYER_RADIUS + GAME_CONFIG.BULLET_RADIUS;

        for (let i = this.bullets.length - 1; i >= 0; i--) {
            const bullet = this.bullets[i];

            // Case 1: Remote bullet hits local player
            if (bullet.ownerId !== this.localPlayerId && localPlayer.alive) {
                const dx = localPlayer.x - bullet.x;
                const dy = localPlayer.y - bullet.y;
                if (Math.sqrt(dx * dx + dy * dy) < threshold) {
                    console.log('[Engine] *** WE GOT HIT by', bullet.ownerId, '***');
                    localPlayer.hp--;
                    this.spawnParticles(bullet.x, bullet.y, '#fff', 12);
                    this.bullets.splice(i, 1);

                    if (localPlayer.hp <= 0) {
                        localPlayer.alive = false;
                        this.spawnParticles(localPlayer.x, localPlayer.y, '#ccc', 20);
                        hits.push({ type: 'self_death', victimId: this.localPlayerId, attackerId: bullet.ownerId });
                    } else {
                        hits.push({ type: 'self_hit', victimId: this.localPlayerId, attackerId: bullet.ownerId });
                    }
                    continue;
                }
            }

            // Case 2: LOCAL bullet hits a REMOTE player (shooter-authoritative)
            if (bullet.ownerId === this.localPlayerId) {
                for (const [playerId, player] of this.players) {
                    if (playerId === this.localPlayerId) continue;
                    if (!player.alive) continue;

                    const dx = player.x - bullet.x;
                    const dy = player.y - bullet.y;
                    if (Math.sqrt(dx * dx + dy * dy) < threshold) {
                        console.log('[Engine] *** OUR BULLET HIT', player.username, '(', playerId, ') ***');
                        this.spawnParticles(bullet.x, bullet.y, '#fff', 15);
                        this.bullets.splice(i, 1);

                        hits.push({ type: 'attack_hit', victimId: playerId, attackerId: this.localPlayerId });
                        break; // bullet can only hit one player
                    }
                }
            }
        }

        return hits;
    }

    spawnParticles(x, y, color, count) {
        for (let i = 0; i < count; i++) {
            this.particles.push(new Particle(x, y, color));
        }
    }

    updateParticles() {
        this.particles = this.particles.filter(p => {
            p.x += p.vx;
            p.y += p.vy;
            p.vx *= 0.95;
            p.vy *= 0.95;
            p.life--;
            return p.life > 0;
        });
    }

    updateDust() {
        for (const d of this.dustMotes) d.update();
    }

    spawnPowerup() {
        const now = Date.now();
        if (now - this.lastPowerupSpawn < GAME_CONFIG.POWERUP_SPAWN_INTERVAL) return;
        if (this.powerups.length >= 5) return;
        this.lastPowerupSpawn = now;

        const spawn = this.getRandomSpawn();
        const type = Math.random() > 0.5 ? 'health' : 'light';
        this.powerups.push(new Powerup(spawn.x, spawn.y, type));
    }

    updateTime() {
        if (this.gameState !== 'playing') return;
        const elapsed = (Date.now() - this.startTime) / 1000;
        this.timeRemaining = Math.max(0, GAME_CONFIG.GAME_DURATION - elapsed);

        if (this.timeRemaining <= 0) {
            this.gameState = 'ended';
        }

        let aliveCount = 0;
        this.players.forEach(p => { if (p.alive) aliveCount++; });
        if (aliveCount <= 1 && this.players.size > 1) {
            this.gameState = 'ended';
        }
    }

    getWinner() {
        let best = null;
        this.players.forEach(p => {
            if (!best || p.kills > best.kills || (p.kills === best.kills && p.alive && !best.alive)) {
                best = p;
            }
        });
        return best;
    }

    addKillFeedEntry(killerName, victimName) {
        this.killFeed.unshift({ killer: killerName, victim: victimName, time: Date.now() });
        if (this.killFeed.length > 5) this.killFeed.pop();
    }

    updateRemotePlayer(id, data) {
        const player = this.players.get(id);
        if (player) {
            player.x = data.x;
            player.y = data.y;
            player.angle = data.angle;
            if (data.hp !== undefined) player.hp = data.hp;
            if (data.alive !== undefined) player.alive = data.alive;
            if (data.kills !== undefined) player.kills = data.kills;
        }
    }
}

export { GameEngine, GAME_CONFIG, PLAYER_COLORS, castFlashlight };
